import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Trophy, Users, Code, Calendar } from 'lucide-react';

const Experience = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

const experiences = [
  {
    title: 'Campus Hackathon',
    role: 'Mentor',
    description: 'Led a team of 4 developers to build an innovative solution. Mentored junior participants and secured 2nd place with our AI-powered Personalized Tourist Guide System for agriculture.',
    achievements: ['2nd Place Winner', 'Mentored 10+ Students', 'Positive Feedback from Judges'],
    date: '2025',
    icon: Trophy,
    type: 'Mentorship'
  },
  {
    title: 'Clockmakers Hackathon',
    role: 'Backend Developer & Team Leader',
    description: 'Contributed to the backend infrastructure and handled the presentation of our self-care solution.',
    achievements: ['Backend Implementation'],
    date: '2025',
    icon: Code,
    type: 'hackathon'
  },
  {
    title: 'College IoT Workshop',
    role: 'Team Lead',
    description: 'Led a team in a comprehensive IoT workshop where we built functional robots and smart devices such as a greeting robot and a smart vacuum cleaner.',
    achievements: ['Built 4+ IoT Projects', 'Successful Workshop Execution'],
    date: '2025',
    icon: Users,
    type: 'leadership'
  },
  {
    title: 'Software Engineering Projects',
    role: 'Full-Stack Developer',
    description: 'Developed several full-stack applications including e-commerce platforms, educational tools, and real-time systems using modern technologies.',
    achievements: ['6+ Projects Completed', 'Utilized Modern Tech Stack'],
    date: '2024-2025',
    icon: Code,
    type: 'projects'
  }
];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="experience" className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="section-title gradient-text">Experience & Leadership</h2>
          <p className="section-subtitle">My journey in tech and leadership</p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="space-y-8"
        >
          {experiences.map((experience, index) => (
            <motion.div
              key={experience.title}
              variants={itemVariants}
              className="relative"
            >
              {/* Timeline Line */}
              {index < experiences.length - 1 && (
                <div className="absolute left-8 top-16 w-0.5 h-16 bg-gradient-to-b from-primary-gold to-primary-violet"></div>
              )}

              <div className="flex gap-6">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-gold to-primary-violet rounded-full flex items-center justify-center">
                    <experience.icon className="text-primary-bg" size={24} />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 glass-effect p-6 rounded-2xl">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-primary-gold font-playfair">
                        {experience.title}
                      </h3>
                      <p className="text-primary-muted font-medium">
                        {experience.role}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-primary-gold mt-2 md:mt-0">
                      <Calendar size={16} />
                      <span className="text-sm font-medium">{experience.date}</span>
                    </div>
                  </div>

                  <p className="text-primary-text mb-4 leading-relaxed">
                    {experience.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {experience.achievements.map((achievement) => (
                      <span
                        key={achievement}
                        className="px-3 py-1 bg-primary-gold/10 text-primary-gold text-sm rounded-full border border-primary-gold/20"
                      >
                        {achievement}
                      </span>
                    ))}
                  </div>

                  {/* Type Badge */}
                  <div className="mt-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      experience.type === 'hackathon' 
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        : experience.type === 'leadership'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                    }`}>
                      {experience.type.charAt(0).toUpperCase() + experience.type.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20"
        >
          <div className="glass-effect p-8 rounded-2xl">
            <h3 className="text-2xl font-bold text-primary-gold mb-8 text-center font-playfair">
              Key Achievements
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { number: '2', label: 'Hackathons', icon: Trophy },
                { number: '10+', label: 'Students Mentored', icon: Users },
                { number: '6+', label: 'Projects Completed', icon: Code },
                { number: '100%', label: 'Workshop Success', icon: Calendar }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-gold/20 to-primary-violet/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="text-primary-gold" size={24} />
                  </div>
                  <div className="text-3xl font-bold text-primary-gold mb-2">
                    {stat.number}
                  </div>
                  <div className="text-primary-muted text-sm">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience; 